import { Fragment, useEffect, useState } from "react";
import { Icon } from "../../kit";
import { useViewer } from "../../../state/ViewerProvider";
import { useDomainState, useUi } from "../../../state/editor/useEditor";
import { useCaps } from "../CapsProvider";
import { downloadBlob, pickFile } from "../../../util/download";
import { drawWatermark } from "../../../util/rasterize";
import { useToast } from "../../../shared/ui";
import { CARD_GAP, RAIL_W } from "../layout";
import { beginSceneRestore } from "../../../state/editor/modelLoads";
import { isJsceneName, sniffSceneFile } from "../../../util/sceneFileSniff";
import { useSceneFileActions } from "../scene/useSceneFileActions";

interface ToolDef {
  icon: string;
  title: string;
  /**
   * May return a promise. If it does, the toolbar disables the button and spins
   * it until settled — Snapshot and Quick export GLB are multi-second renders
   * that previously gave no sign of running and could be re-fired on every
   * click, queueing several full-resolution captures at once.
   */
  run: () => void | Promise<unknown>;
  active?: boolean;
  disabled?: boolean;
}

/** Floating canvas toolbar (iJewel right-edge stack). v12-able actions are
 * live; the rest are disabled until their engine wave lands (caps-gated). */
export function SceneToolbar({
  onFullscreen,
  rightInset = RAIL_W,
}: {
  onFullscreen: () => void;
  /** Width the chrome occupies on the right, so the stack sits INSIDE the
   *  canvas card's edge the way the reference's does. */
  rightInset?: number;
}) {
  const { client } = useViewer();
  const r = client.ring;
  const caps = useCaps();
  const [ui, patchUi] = useUi();
  const models = useDomainState("models");
  const selection = useDomainState("selection");
  const [rotating, setRotating] = useState(false);
  /** Title of the tool whose async run is in flight (null = idle). */
  const [busyTool, setBusyTool] = useState<string | null>(null);
  const { toast } = useToast();
  const sceneFiles = useSceneFileActions();

  useEffect(() => {
    void r.getControlsParams().then((p) => setRotating(!!p.autoRotate));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const hasModel = models.length > 0;
  const soon = (title: string) => `${title} — coming soon`;

  const tools: ToolDef[] = [
    {
      icon: "cached",
      title: "Replace model",
      // pickFile resolves when the IMPORT finishes (or the dialog is cancelled),
      // so returning it here spins the button for the real duration.
      run: () =>
        pickFile(caps.sceneFileV65 ? ".glb,.gltf,.obj,.fbx,.stl,.3dm,.jscene" : ".glb,.gltf,.obj,.fbx,.stl,.3dm", async (f) => {
          try {
            // v65: a scene file with no ring in it replaces the LOOK, not the
            // ring — the same undoable open as Output › Whole scene.
            const sniff = caps.sceneFileV65 && /\.(glb|jscene)$/i.test(f.name) ? await sniffSceneFile(f) : null;
            if (caps.sceneFileV65 && (isJsceneName(f.name) || (sniff && !sniff.hasModel))) {
              await sceneFiles.openFile(f);
              return;
            }
            // A "Ring + scene" file brings its own look: open the restore window
            // before the model is added, so the materials dialog never asks.
            const restore = sniff ? beginSceneRestore(client) : null;
            try {
              const existing = await r.listModels();
              await Promise.all(existing.map((m) => r.removeModel(m.id)));
              const id = await r.addModelFile(f, ui.dropOptions);
              restore?.claim(id);
            } finally {
              restore?.end();
            }
          } catch (err) {
            toast({ title: "Could not replace the model", description: err instanceof Error ? err.message : String(err), tone: "error" });
          }
        }),
    },
    {
      icon: "delete",
      title: caps.toolbarOps ? "Delete selected" : soon("Delete"),
      disabled: !caps.toolbarOps || !selection,
      run: () => void (caps.toolbarOps && client.call("deleteSelected")),
    },
    { icon: "center_focus_strong", title: "Frame model", disabled: !hasModel, run: () => void r.autoFit() },
    {
      icon: "restart_alt",
      title: caps.toolbarOps ? "Reset rotation" : soon("Reset rotation"),
      disabled: !caps.toolbarOps || !hasModel,
      run: () => void (caps.toolbarOps && client.call("resetSelectedRotation")),
    },
    {
      // Rhino's four-up: Top / Front / Right locked to their axes, plus a free
      // 3-D pane. Each pane carries its own unlock switch (ViewportPaneSwitches).
      icon: "grid_view",
      title: caps.viewportQuadV60
        ? (ui.viewportLayout === "quad" ? "Single viewport" : "Split viewport (Top / Front / Right / 3D)")
        : soon("Split viewport"),
      active: ui.viewportLayout === "quad",
      disabled: !caps.viewportQuadV60 || !hasModel,
      run: () => {
        if (!caps.viewportQuadV60) return;
        const next = ui.viewportLayout === "quad" ? "single" : "quad";
        patchUi({ viewportLayout: next });
        void r.setViewportLayout(next);
      },
    },
    {
      icon: "play_arrow",
      title: "Turntable",
      active: rotating,
      disabled: !hasModel,
      run: () => {
        const next = !rotating;
        setRotating(next);
        void r.setAutoRotate(next, 1.5);
      },
    },
    {
      icon: "flip",
      title: caps.toolbarOps ? "Mirror" : soon("Mirror"),
      disabled: !caps.toolbarOps || !hasModel,
      run: () => void (caps.toolbarOps && client.call("mirrorSelected", "x")),
    },
    { icon: "recenter", title: "Center / fit", disabled: !hasModel, run: () => void r.autoCenter() },
    {
      icon: "photo_camera",
      title: "Snapshot (uses the Renderer size)",
      disabled: !hasModel,
      run: async () => {
        try {
          // Honour the RENDERER section's Width/Height instead of shooting a
          // fixed 1600. Setting Mode=Custom 2000x2000 and then getting a 1600px
          // file back is the whole complaint: the size control worked and the
          // snapshot API honoured it — this button just never asked.
          //
          // Custom mode with an explicit size wins; otherwise fall back to the
          // live canvas size so the shot matches what is on screen, and only
          // then to a square default.
          const p = await r.getRendererParams().catch(() => null);
          const custom = p?.mode === "custom" && p.width > 0 && p.height > 0;
          const width = custom ? p.width : (p?.width || 1600);
          const height = custom ? p.height : (p?.height || 1600);
          let shot = await r.snapshot({ width, height });
          // Same rule as the Export panel: if the watermark is on, it is IN the
          // file. A watermark that appears on screen and not in the download is
          // the bug this whole slice exists to fix.
          if (ui.watermark.on && ui.watermark.text) {
            shot = await drawWatermark(shot, ui.watermark.text, "image/png", ui.watermark.light);
          }
          downloadBlob(shot, `jewelry_${width}x${height}.png`);
        } catch (err) {
          toast({ title: "Snapshot failed", description: String(err), tone: "error" });
        }
      },
    },
    {
      icon: "download",
      title: "Quick export GLB",
      disabled: !hasModel,
      run: async () => {
        try {
          downloadBlob(await r.exportGLB({ compress: true }), "jewelry.glb");
        } catch (err) {
          toast({ title: "GLB export failed", description: String(err), tone: "error" });
        }
      },
    },
  ];

  /** Just inside the card, which already starts `CARD_GAP` in from the chrome. */
  const stackRight = rightInset + CARD_GAP + 8;

  const runTool = (t: ToolDef) => {
    if (busyTool) return;
    let res: void | Promise<unknown>;
    try {
      res = t.run();
    } catch {
      return;
    }
    if (!res || typeof (res as Promise<unknown>).then !== "function") return;
    setBusyTool(t.title);
    (res as Promise<unknown>).finally(() => setBusyTool(null));
  };

  return (
    <>
      {/* The reference floats fullscreen alone at the card's top-right corner,
          separate from the tool stack. */}
      <button
        title="Fullscreen"
        onClick={onFullscreen}
        className="pointer-events-auto absolute z-20 flex h-9 w-9 items-center justify-center rounded-full border border-ed-border bg-ed-panel/95 text-ed-text-2 shadow-[0_4px_16px_rgba(0,0,0,0.08)] backdrop-blur-sm transition-colors hover:bg-ed-hover hover:text-ed-text"
        style={{ top: CARD_GAP + 12, right: stackRight }}
      >
        <Icon name="fullscreen" size={18} />
      </button>
      <div
        className="pointer-events-auto absolute top-1/2 z-20 flex -translate-y-1/2 flex-col rounded-xl border border-ed-border bg-ed-panel/95 p-1 shadow-[0_8px_24px_rgba(0,0,0,0.08)] backdrop-blur-sm"
        style={{ right: stackRight }}
      >
      {tools.map((t, i) => {
        const busy = busyTool === t.title;
        // While one tool is running the others are held too: they share one
        // renderer, so a second capture mid-snapshot fights the first.
        const off = t.disabled || busyTool !== null;
        return (
          <Fragment key={t.icon + t.title}>
            {/* A hairline between every button, as the reference draws them. */}
            {i > 0 ? <span className="mx-1.5 my-0.5 h-px bg-ed-border" /> : null}
          <button
            title={busy ? `${t.title}…` : t.title}
            disabled={off}
            aria-busy={busy || undefined}
            onClick={() => runTool(t)}
            className={`flex h-8 w-8 items-center justify-center rounded-lg transition-colors ${
              busy
                ? "bg-ed-active text-ed-text"
                : t.active
                  ? "bg-ed-active text-ed-text"
                  : off
                    ? "cursor-not-allowed text-ed-text-3 opacity-40"
                    : "text-ed-text-2 hover:bg-ed-hover hover:text-ed-text"
            }`}
          >
            <Icon
              name={busy ? "progress_activity" : t.icon}
              size={18}
              className={busy ? "animate-spin" : undefined}
            />
          </button>
          </Fragment>
        );
      })}
      </div>
    </>
  );
}
